<script setup lang="ts">
import type { RouteLocationRaw } from '#vue-router'
import type { CMSButtonAttributes } from '#shared/types/cmsLayout'
import { match } from 'ts-pattern'
import type { UIProp } from '~/types/elementsUi'

interface ExtendedCMSButtonAttributes extends CMSButtonAttributes {
  color?: ButtonColor
  darkModeColor?: BaseTailwindColor
}

interface VButtonRendererProps {
  attributes?: ExtendedCMSButtonAttributes
  ui?: UIProp<'button'>
}

const props = defineProps<VButtonRendererProps>()

const localeStore = useLocaleStore()

const computedTo = computed(() => {
  return match(props.attributes)
    .with({ type: 'internal' }, (attribute) => `/${attribute.url}`)
    .with({ type: 'phone' }, (attribute) => `tel:${attribute.url}`)
    .with({ type: 'email' }, (attribute) => `mailto:${attribute.url}`)
    .with({ type: 'external' }, { type: 'pdf' }, (attribute) => attribute.url)
    .otherwise(() => undefined)
})

const computedTarget = computed(() => {
  return match(props.attributes?.type)
    .with('external', 'pdf', () => '_blank')
    .otherwise(() => undefined)
})

const computedDownload = computed(() => {
  return match(props.attributes?.type)
    .with('pdf', () => '')
    .otherwise(() => undefined)
})

const computedLabel = computed(() => props.attributes?.[localeStore.locale])
</script>

<template>
  <VButton
    v-if="attributes"
    :label="computedLabel"
    :to="computedTo"
    :target="computedTarget"
    :download="computedDownload"
    :color="attributes.color"
    :dark-mode-color="attributes.darkModeColor"
    :class="ui?.button"
    :icon-left="attributes.iconLeft"
    :icon-right="attributes.iconRight"
  />
</template>

<style scoped></style>
